import React from 'react';

/**
 * Lightweight inline SVG icons used across the redesigned homepage sections.
 * They inherit `currentColor` so colours are driven entirely by CSS, keeping
 * everything on-brand for Ethy.
 */

const base = {
  width: 28,
  height: 28,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
};

// Claims checked against evidence — shield with a checkmark
export const IconVerified = props => (
  <svg {...base} {...props}>
    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

// Verified credentials — leaf
export const IconLeaf = props => (
  <svg {...base} {...props}>
    <path d="M5 19c8 1 14-4 14-14 0 0-11-2-13 6-1 4 -1 8-1 8z" />
    <path d="M5 19c3-6 6-8 10-10" />
  </svg>
);

// Trusted brands — storefront
export const IconStore = props => (
  <svg {...base} {...props}>
    <path d="M4 9l1-4h14l1 4" />
    <path d="M4 9v0a2 2 0 004 0 2 2 0 004 0 2 2 0 004 0 2 2 0 004 0" />
    <path d="M5 9v10h14V9" />
    <path d="M9 19v-5h6v5" />
  </svg>
);

// Transparent impact — trend chart
export const IconImpact = props => (
  <svg {...base} {...props}>
    <path d="M4 19V5" />
    <path d="M4 19h16" />
    <path d="M7 15l3-4 3 2 4-6" />
  </svg>
);

// Category — Home
export const IconHome = props => (
  <svg {...base} {...props}>
    <path d="M4 11l8-6 8 6" />
    <path d="M6 10v9h12v-9" />
    <path d="M10 19v-5h4v5" />
  </svg>
);

// Category — Fashion (hanger)
export const IconFashion = props => (
  <svg {...base} {...props}>
    <path d="M12 6a2 2 0 112 2c-1 0-2 .8-2 2" />
    <path d="M12 10L4 16v2h16v-2l-8-6z" />
  </svg>
);

// Category — Beauty (droplet)
export const IconBeauty = props => (
  <svg {...base} {...props}>
    <path d="M12 3c3 4 5 6.5 5 9.5A5 5 0 017 12.5C7 9.5 9 7 12 3z" />
  </svg>
);

// Category — Food (bowl)
export const IconFood = props => (
  <svg {...base} {...props}>
    <path d="M3 11h18" />
    <path d="M4 11a8 8 0 0016 0" />
    <path d="M12 4c1.5 1 1.5 2.5 0 3.5" />
  </svg>
);

// Category — Gifts
export const IconGift = props => (
  <svg {...base} {...props}>
    <path d="M4 9h16v11H4z" />
    <path d="M4 9l1-3h14l1 3" />
    <path d="M12 6v14" />
    <path d="M12 6c-1-2-4-2-4 0s3 0 4 0zm0 0c1-2 4-2 4 0s-3 0-4 0z" />
  </svg>
);

// Category — Outdoors (mountains)
export const IconOutdoors = props => (
  <svg {...base} {...props}>
    <path d="M3 19l6-10 4 6" />
    <path d="M11 19l4-8 6 8z" />
  </svg>
);

// Generic arrow used for CTAs / links
export const IconArrow = props => (
  <svg {...base} width={18} height={18} {...props}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

// Chevrons used by the horizontally scrolling product rows
export const IconChevronLeft = props => (
  <svg {...base} width={20} height={20} strokeWidth={2} {...props}>
    <path d="M14 6l-6 6 6 6" />
  </svg>
);

export const IconChevronRight = props => (
  <svg {...base} width={20} height={20} strokeWidth={2} {...props}>
    <path d="M10 6l6 6-6 6" />
  </svg>
);

// Category — fallback (tag) for any category we don't have bespoke artwork for.
// Categories come from the hosted Sharetribe config, so operators can add ones
// this file has never heard of; they still get a tidy icon rather than a hole.
export const IconTag = props => (
  <svg {...base} {...props}>
    <path d="M4 4h7l9 9-7 7-9-9V4z" />
    <path d="M8.5 8.5h.01" />
  </svg>
);

export const categoryIcons = {
  home: IconHome,
  homeware: IconHome,
  fashion: IconFashion,
  clothing: IconFashion,
  beauty: IconBeauty,
  skincare: IconBeauty,
  food: IconFood,
  drink: IconFood,
  fooddrink: IconFood,
  gift: IconGift,
  gifts: IconGift,
  outdoors: IconOutdoors,
  garden: IconOutdoors,
};

/**
 * Pick an icon for a hosted category, matching on its id first and then its
 * display name (both loosely, so "Food & drink" finds `fooddrink`).
 *
 * @param {...string} candidates category id / name
 * @returns {Function} an icon component — never null
 */
export const getCategoryIcon = (...candidates) => {
  const normalize = value =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const match = candidates.map(normalize).find(candidate => !!categoryIcons[candidate]);
  return match ? categoryIcons[match] : IconTag;
};
